/*
 * Le Grand Jeu Monopoly 2025 — the figures the year-on-year tab compares against.
 *
 * WHERE THEY COME FROM: the client's own Power BI export of last year's
 * campaign ("Realtime Dashboard (Monopoly)", 09/09/2025 – 13/10/2025). There is
 * no 2025 pipeline and no 2025 database to query, so the numbers are carried
 * here as data rather than computed. They are frozen: last year's campaign is
 * over and these will not change.
 *
 * HOW EXACT THEY ARE, day by day. The export prints a value above most bars but
 * not all of them, so:
 *   - most days are the printed figure, exact;
 *   - the rest (listed in `derivedDays` per metric) are read off the bar heights
 *     and scaled so the series adds up to the export's own printed total. Each
 *     printed label lands within 1% of its measured bar, so the derived days
 *     carry about that much uncertainty. `TOTALS` below are the export's printed
 *     totals and are exact.
 *   - two participant bars and one consent bar were CUT OFF by the top of their
 *     plot; for those the printed label is the only truth, and it is used.
 *
 * THE 2025 CONSENT GAP IS REAL. Last year's consent chart has no 09/09 and no
 * 25/09–01/10: those categories are absent from the export, not zero. They are
 * `null` here and the tab draws a gap rather than inventing a floor of zero.
 *
 * ALIGNMENT: index 0 is campaign day 1 (09/09/2025), and 2026 day 1 is
 * 08/09/2026 — both campaigns are 35 days and both open on a Tuesday, so
 * comparing by campaign day also compares like weekday with like.
 */

/** Day 1 of the 2025 campaign; index i in every series below is day i+1. */
export const LAST_YEAR_START = '2025-09-09';
export const LAST_YEAR_DAYS = 35;

/** The export's own printed totals for the full 35 days. */
export const LAST_YEAR_TOTALS = {
  participants: 1_326_970,
  visits: 4_690_564,
  consents: 1_339_897,
} as const;

/** Participants uniques — "Registrations" in the 2025 export. */
export const LAST_YEAR_PARTICIPANTS: Array<number | null> = [
  102_900, 79_449, 71_401, 76_077, 95_007, 51_124, 51_104, 42_357, 42_860, 40_475, 39_437, 56_911, 33_436, 33_537, 33_507, 32_928, 27_486, 29_987, 40_324,
  24_017, 23_190, 22_983, 24_973, 23_261, 21_117, 27_133, 20_876, 19_333, 18_525, 20_855, 17_979, 19_911, 25_900, 18_724, 17_886,
];

/** Visites — "Visits" in the 2025 export. */
export const LAST_YEAR_VISITS: Array<number | null> = [
  108_715, 126_298, 147_232, 152_177, 172_308, 111_930, 119_296, 126_686, 128_039, 117_056, 130_084, 175_278, 105_928, 113_507, 129_467, 128_537, 115_783,
  137_202, 174_695, 103_069, 112_035, 127_079, 137_389, 124_609, 136_584, 161_416, 112_290, 121_837, 128_337, 136_309, 133_191, 158_733, 196_715, 129_592,
  151_161,
];

/**
 * Consentements — "Consent" in the 2025 export, every type summed, which is the
 * same thing this year's `consentsGranted` counts. `null` = no figure recorded
 * that day (see the gap note above).
 */
export const LAST_YEAR_CONSENTS: Array<number | null> = [
  null,
  10_646,
  42_144,
  68_546,
  114_334,
  59_939,
  61_441,
  53_034,
  57_216,
  55_769,
  58_125,
  84_179,
  49_771,
  50_417,
  54_523,
  20_758,
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  107,
  2_327,
  66_485,
  50_693,
  47_741,
  46_261,
  48_276,
  42_660,
  49_759,
  62_060,
  41_743,
  40_943,
];

/** Campaign days (1-based) whose figure is read from the bar, not printed on it. */
export const LAST_YEAR_DERIVED_DAYS = {
  participants: [7, 9, 11, 15, 16, 18, 21, 23, 25, 27, 28, 32, 35],
  visits: [4, 5, 9, 23, 28, 31],
  consents: [7, 10, 14, 28, 30, 35],
} as const;

export type ComparisonMetric = 'participants' | 'visits' | 'consents';

export const LAST_YEAR_SERIES: Record<ComparisonMetric, Array<number | null>> = {
  participants: LAST_YEAR_PARTICIPANTS,
  visits: LAST_YEAR_VISITS,
  consents: LAST_YEAR_CONSENTS,
};

/** ISO date of 2025's campaign day `day` (1-based) — for the hover tooltip. */
export function lastYearDate(day: number): string {
  const t = Date.parse(`${LAST_YEAR_START}T00:00:00Z`) + (day - 1) * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}
